import { useState, useEffect, useMemo } from "react";
import { BiPlus } from "react-icons/bi";
import { GrAscend, GrDescend } from "react-icons/gr";
import { PiMagnifyingGlass } from "react-icons/pi";
import NewOrganizationModal from "./NewOrganizationModal";
import { useNavigate } from "react-router-dom";
import { VscOrganization } from "react-icons/vsc";
import ItemsLoader from "../Loaders/ItemsLoading";
import { TbReload } from "react-icons/tb";
import { useLanguage } from "../Translations/LenguageContext";

const MainOrganizations = ({
    account_id,
    userData,
    loading,
    data,
    fetchData
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");

    useEffect(() => {
        fetchData();
    }, [account_id]);

    const filteredAndSortedData = useMemo(() => {
        if (!data) return [];

        let result = data.filter((org) => {
            const term = searchTerm.toLowerCase();
            const nameMatch = org.name?.toLowerCase().includes(term);
            const descMatch = org.description?.toLowerCase().includes(term);

            const memberMatch = org.members?.some(m =>
                m.name?.toLowerCase().includes(term) ||
                m.surname?.toLowerCase().includes(term) ||
                m.email?.toLowerCase().includes(term)
            );

            return nameMatch || descMatch || memberMatch;
        });

        result.sort((a, b) => {
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            if (sortOrder === "asc") {
                return nameA.localeCompare(nameB);
            } else {
                return nameB.localeCompare(nameA);
            }
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    const [imgError, setImgError] = useState(false);



    if (loading) {
        return <ItemsLoader />;
    }

    return (
        <div className="mainframe_item" >
            <div className="mainframe_item_header" >
                <p className="mainframe_item_title fade-in">{t.organizations.title}</p>
                <div className="mainframe_item_subtitle fade-in">
                    {data.length === 0 ? (
                        <div className="empty_org_mex_cont">
                            <p>{t.organizations.empty_subtitle}</p>
                            <div className="empty_org_img">
                                <p className="empty_org_img_title">
                                    <i><VscOrganization /></i> {t.organizations.quest_title}
                                </p>
                                <p className="empty_org_img_subtitle">{t.organizations.quest_answ}</p>
                            </div>
                        </div>
                    ) : (
                        <p>{t.organizations.subtitle}</p>
                    )}
                </div>
                 <button
                    className={`mainframe_item_create_btn fade-in ${data.length === 0 && "mainframe_item_create_btn_anim"}`}
                    onClick={() => setOpenModal(true)}
                >
                    <BiPlus /> {t.create}
                </button>
            </div>

            {(data.length > 0 || searchTerm) && (
                <div className="filter_body_mainframe">
                    <div className="filtering_mainframe">
                        <div className="filtering_mainframe_btn_cont fade-in">
                            {data.length > 1 && (
                                <>
                                    <button
                                        className={`filter_btn ${sortOrder === "asc" ? "active" : ""}`}
                                        onClick={() => setSortOrder("asc")}
                                        title="A-Z"
                                    >
                                        <GrAscend />
                                    </button>
                                    <button
                                        className={`filter_btn ${sortOrder === "desc" ? "active" : ""}`}
                                        onClick={() => setSortOrder("desc")}
                                        title="Z-A"
                                    >
                                        <GrDescend />
                                    </button>
                                </>
                            )}
                            <button
                                className="filter_btn"
                                onClick={() => fetchData()}
                                title={t.reload}
                            >
                                <TbReload />
                            </button>
                        </div>
                        <div className="filter_input_cont fade-in">
                            <input
                                className="filter_input"
                                placeholder={t.search_filter}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                        </div>
                    </div>

                    <div className="organizations_cont">
                        {filteredAndSortedData.length > 0 ? (
                            <div className="organizations_cont_inner">
                                {filteredAndSortedData.map((org) => (
                                    <div
                                        key={org.id}
                                        className="organizations_item fade-in"
                                        onClick={() => navigate(`/console/organizations/${org.id}/geo`)}
                                    >
                                        <p className="organizations_item_title" title={org.name}>
                                            {org.name}
                                        </p>
                                        <p className="organizations_item_desc" title={org.description}>
                                            {org.description}
                                        </p>
                                        <div className="avatar_group">
                                            {org.members.slice(0, 4).map((member, index) => {
                                                const initials = `${member.name?.[0] || ""}${member.surname?.[0] || ""}`.toUpperCase();
                                                return (
                                                    <div
                                                        key={member.id}
                                                        className="avatar"
                                                        style={{
                                                            zIndex: org.members.length - index,
                                                            marginLeft: index === 0 ? 0 : -10
                                                        }}
                                                        title={`${member.name} ${member.surname} (${member.email})`}
                                                    >
                                                        {
                                                            member.profile_url && !imgError ? (
                                                                <img
                                                                    src={member.profile_url}
                                                                    alt={member.name}
                                                                    className="avatar_img"
                                                                    onError={() => setImgError(true)}
                                                                />
                                                            ) : (
                                                                <span className="avatar_initials">{initials}</span>
                                                            )
                                                        }
                                                    </div>
                                                );
                                            })}
                                            {org.members.length > 4 && (
                                                <div className="avatar more_count">
                                                    +{org.members.length - 4}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="organizations_cont_no_items_mex fade-in">
                                <p>{t.no_data_found_by_search}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <NewOrganizationModal
                account_id={account_id}
                show={openModal}
                handleClose={() => setOpenModal(false)}
                fetchRecords={fetchData}
            />
        </div>
    );
};

export default MainOrganizations;